/**
 * Error handling utility functions
 * Provides centralized error message extraction and processing
 */

import { UploadStatus } from '../types/DrawData';

// Backward compatibility constants
export const UPLOAD_STATUS = UploadStatus;
export type { UploadStatus };

// Error messages
export const ERROR_MESSAGES = {
  NO_DRAW_ID: "Draw ID is required",
  UPLOAD_FAILED: "Upload failed"
};

/**
 * Validate draw ID and return target draw ID
 * @param set - Zustand set function
 * @param drawId - Optional draw ID parameter
 * @param currentDrawId - Current draw ID from state
 * @param errorContext - Context for error message
 * @returns Valid draw ID or null if invalid
 */
export const validateDrawId = (
  set: any,
  drawId: string | undefined,
  currentDrawId: string | undefined,
  errorContext: string
): string | null => {
  const targetDrawId = drawId || currentDrawId;
  
  if (!targetDrawId) {
    set({
      status: UploadStatus.ERROR,
      error: ERROR_MESSAGES.NO_DRAW_ID,
    });
    return null;
  }
  
  return targetDrawId;
};

/**
 * Set error state with proper error extraction
 * @param set - Zustand set function
 * @param error - Error object to extract message from
 * @param defaultMessage - Default error message
 * @param preserveFile - Whether to preserve selected file in state
 */
export const setErrorState = (
  set: any,
  error: any,
  fallbackMessage: string,
  keepFile = false
): void => {
  const errorMessage = extractHttpErrorMessage(error, fallbackMessage);
  
  if (keepFile) {
    set({
      status: UploadStatus.ERROR,
      error: errorMessage,
    });
  } else {
    set({
      status: UploadStatus.ERROR,
      error: errorMessage,
      entriesUpload: {},
      selectedFile: undefined,
    });
  }
};

/**
 * Extract stored filename from Uppy upload response
 * @param uploadResponse - Uppy upload response object
 * @param set - Zustand set function for error handling
 * @returns Extracted filename or null if invalid
 */
export const extractStoredFilenameFromUppyResponse = (
  uploadResponse: any,
  set: any
): string | null => {
  if (!uploadResponse || typeof uploadResponse !== 'object') {
    set({
      status: UploadStatus.ERROR,
      error: `Invalid upload response: expected object, received ${typeof uploadResponse}`,
    });
    return null;
  }
  
  const responseData = uploadResponse.body || uploadResponse;
  
  if (!responseData || typeof responseData !== 'object') {
    set({
      status: UploadStatus.ERROR,
      error: `Invalid response data structure: expected object in body, received ${typeof responseData}`,
    });
    return null;
  }
  
  const storedFileNameWithExtension = responseData.storedFileName;
  
  if (!storedFileNameWithExtension || typeof storedFileNameWithExtension !== 'string') {
    const availableKeys = Object.keys(responseData);
    set({
      status: UploadStatus.ERROR,
      error: `Missing or invalid storedFileName in response body. Available keys: [${availableKeys.join(', ')}]`,
    });
    return null;
  }

  return storedFileNameWithExtension;
};

/**
 * Extract human-readable error message from various error types
 * Handles axios errors, Error objects, strings, and provides fallback
 * 
 * @param error - The error object (can be axios error, Error, string, or unknown)
 * @param defaultMessage - Fallback message if no specific error can be extracted
 * @returns A human-readable error message string
 */
export const extractHttpErrorMessage = (error: unknown, defaultMessage: string): string => {
  // Handle axios errors with response data
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as any;
    
    // Try to extract error details in order of preference
    if (axiosError.response?.data?.detail) {
      return axiosError.response.data.detail;
    }
    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message;
    }
    if (axiosError.response?.data?.error) {
      return axiosError.response.data.error;
    }
    
    // Fallback to status text for HTTP errors
    if (axiosError.response?.statusText) {
      return `HTTP ${axiosError.response.status}: ${axiosError.response.statusText}`;
    }
  }
  
  // Handle regular Error objects
  if (error instanceof Error) {
    return error.message;
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }
  
  // Fallback to default message
  return defaultMessage;
};

/**
 * Check if an error indicates an authentication/authorization issue
 * 
 * @param error - The error object to check
 * @returns True if the error appears to be auth-related
 */
export const isAuthenticationError = (error: unknown): boolean => {
  const message = extractHttpErrorMessage(error, '').toLowerCase();
  
  return message.includes('expired') || 
         message.includes('unauthorized') || 
         message.includes('forbidden') ||
         message.includes('authentication') ||
         message.includes('token');
};

/**
 * Check if an error indicates a network connectivity issue
 * 
 * @param error - The error object to check
 * @returns True if the error appears to be network-related
 */
export const isNetworkError = (error: unknown): boolean => {
  if (error && typeof error === 'object' && 'code' in error) {
    const networkError = error as any;
    return networkError.code === 'NETWORK_ERROR' || 
           networkError.code === 'ECONNABORTED' ||
           networkError.code === 'TIMEOUT';
  }
  
  const message = extractHttpErrorMessage(error, '').toLowerCase();
  return message.includes('network') || 
         message.includes('timeout') ||
         message.includes('connection');
};

/**
 * Get user-friendly error message with suggestions
 * 
 * @param error - The error object
 * @param context - Additional context about what was being attempted
 * @returns Enhanced error message with potential solutions
 */
export const getEnhancedErrorMessage = (error: unknown, context?: string): string => {
  const baseMessage = extractHttpErrorMessage(error, 'An unexpected error occurred');
  
  if (isAuthenticationError(error)) {
    return `${baseMessage} Please refresh the page and try again.`;
  }
  
  if (isNetworkError(error)) {
    return `${baseMessage} Please check your internet connection and try again.`;
  }
  
  if (context) {
    return `${context}: ${baseMessage}`;
  }
  
  return baseMessage;
};